/** @file How many values of a document's existing rig entities a plan changes (the re-rig line). */
import type { Doc } from '@pose2d/format/types';

/** The rig's tables; an entity only one document has (created or deleted by the plan) is not counted. */
const TABLES = ['bones', 'slots', 'attachments', 'constraints', 'sockets'] as const;
/** Smaller differences are rounding noise, not a change the person would see. */
const EPSILON = 1e-6;

/** Differing leaves of two JSON values: numbers beyond EPSILON, other primitives by identity, objects and arrays per key. */
function diff(a: unknown, b: unknown): number {
  if (typeof a === 'number' && typeof b === 'number') return Math.abs(a - b) > EPSILON ? 1 : 0;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return Object.is(a, b) ? 0 : 1;
  const was = a as Readonly<Record<string, unknown>>;
  const now = b as Readonly<Record<string, unknown>>;
  let n = 0;
  for (const key of new Set([...Object.keys(was), ...Object.keys(now)])) n += diff(was[key], now[key]);
  return n;
}

/** Values (setup transforms, lengths, attachment fields, …) of the entities both documents have that `after` changes. */
export function changedValues(before: Doc, after: Doc): number {
  let n = 0;
  for (const table of TABLES) {
    const was: Readonly<Record<string, unknown>> = before[table];
    const now: Readonly<Record<string, unknown>> = after[table];
    for (const [id, entity] of Object.entries(was)) if (id in now) n += diff(entity, now[id]);
  }
  return n;
}
